import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";

export default function KategoriSide() {
  const { categorySlug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [products, setProducts] = useState([]);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    setLightboxIndex(null);

    // Map URL slug to display title for initial tab title
    const displayTitle = getCategoryDisplayTitle(categorySlug);
    document.title = `${displayTitle} - Havneloppen`;

    // Fetch page data, products, and gallery in parallel
    Promise.all([
      fetch("https://api.dev.auxo.dk/public/content/sider").then((res) => res.json()),
      fetch("https://api.dev.auxo.dk/public/content/produkter").then((res) => res.json()),
      fetch("https://api.dev.auxo.dk/public/content/galleri").then((res) => res.json()),
    ])
      .then(([sider, produkter, gallerier]) => {
        // Find page data
        if (Array.isArray(sider)) {
          const clean = (str) => (str || "").toLowerCase().trim().replace(/[^a-z0-9æøå]/gi, "-");
          const target = clean(categorySlug);
          const page = sider.find((p) => clean(p.url_slug) === target || clean(p.titel) === target);
          setPageData(page);
          if (page) {
            document.title = page.meta_titel || `${page.titel} - Havneloppen`;
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc && page.meta_beskrivelse) {
              metaDesc.setAttribute("content", page.meta_beskrivelse);
            }
          }
        }

        // Find products in this category
        if (Array.isArray(produkter)) {
          const clean = (str) => (str || "").toLowerCase().trim().replace(/[^a-z0-9æøå]/gi, "-");
          const target = clean(categorySlug);
          const catProducts = produkter.filter((p) => {
            const pCatSlug = clean(p.terms?.kategori?.[0]);
            const pCatText = clean(p.kategori);
            return pCatSlug === target || pCatText.includes(target) || target.includes(pCatSlug);
          });
          setProducts(catProducts);
        }

        // Find gallery connected to this category
        if (Array.isArray(gallerier)) {
          const catGallery = gallerier.find((g) => g.url_slug === categorySlug);
          setGallery(catGallery);
        }
      })
      .catch((err) => console.error("Error loading category page data:", err))
      .finally(() => setLoading(false));
  }, [categorySlug]);

  function getCategoryDisplayTitle(slug) {
    const slugMap = {
      "billeder-film-og-lyd": "Billeder/film og lyd",
      "bornenes-univers": "Børnenes univers",
      "design": "Design",
      "elektonik": "Elektonik",
      "lamper": "Lamper",
      "laesestof": "Læsestof",
      "malerier": "Malerier",
      "mobler": "Møbler",
      "porcelan-og-keramik": "Porcelæn og keramik",
      "smating-til-hjemmet": "Småting til hjemmet",
    };
    if (slugMap[slug]) return slugMap[slug];
    if (pageData?.titel) return pageData.titel;
    return slug ? slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ") : "Kategori";
  }

  // Lightbox navigation
  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (!gallery?.billeder) return;
    setLightboxIndex((prev) => (prev === 0 ? gallery.billeder.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (!gallery?.billeder) return;
    setLightboxIndex((prev) => (prev === gallery.billeder.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange" />
          <span className="text-sm font-medium text-dark-graphite-light">Henter kategori...</span>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-20 text-center">
        <h2 className="font-display text-2xl font-bold text-brand-blue mb-4">Kategorien blev ikke fundet</h2>
        <p className="text-dark-graphite-light mb-6">Vi kunne desværre ikke finde den kategori, du leder efter.</p>
        <Link to="/kategorier" className="rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-bold text-warm-white">
          Se alle kategorier
        </Link>
      </div>
    );
  }

  // Optimize hero image
  const heroImage = pageData.billede?.[0] || "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/732789416-3930006427136378-8236971886744770619-n.jpg";
  const optimizedHeroImage = heroImage.replace(
    "assets.auxodev.dk/assets",
    "assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets"
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Category Header / Hero */}
      <section className="relative bg-brand-blue text-warm-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedHeroImage}
            alt={pageData.titel}
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <Link to="/kategorier" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-orange hover:text-warm-white transition-colors mb-4">
            <i className="fa-solid fa-arrow-left" />
            Tilbage til alle kategorier
          </Link>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-warm-white sm:text-4xl md:text-5xl">
            {pageData.titel}
          </h1>
        </div>
      </section>

      {/* Category Intro Section */}
      <section className="py-12 md:py-16 bg-warm-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="prose text-dark-graphite-light"
            dangerouslySetInnerHTML={{ __html: pageData.indhold || "" }}
          />
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="py-12 md:py-16 bg-warm-beige/20 border-t border-warm-beige">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-10">
            <h2 className="font-display text-2xl font-bold text-brand-blue">
              Aktuelle fund i {getCategoryDisplayTitle(categorySlug).toLowerCase()}
            </h2>
            <p className="text-sm text-dark-graphite-light mt-1">
              Her kan du se de varer, vi har på hylderne lige nu inden for denne kategori.
            </p>
          </div>

          {products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-warm-beige bg-white p-12 text-center max-w-md mx-auto">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warm-beige text-dark-graphite/40 mx-auto mb-4">
                <i className="fa-solid fa-box-open text-xl" />
              </div>
              <h3 className="font-display text-lg font-bold text-brand-blue mb-1">Ingen varer i øjeblikket</h3>
              <p className="text-sm text-dark-graphite-light">
                Vi har i øjeblikket ingen registrerede varer i denne kategori. Men kig endelig forbi butikken – vi får nye ting ind hver dag!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Connected Gallery Section */}
      {gallery && gallery.billeder && gallery.billeder.length > 0 && (
        <section className="py-16 md:py-24 bg-white border-t border-warm-beige">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-12">
              <h2 className="font-display text-2xl font-bold text-brand-blue mb-2">
                Stemningsbilleder fra afdelingen
              </h2>
              <p className="text-sm text-dark-graphite-light">
                {gallery.beskrivelse || "Se billeder af vores hyggelige butiksafdeling og tidligere unikke fund."}
              </p>
            </div>

            {/* Grid of Gallery Images */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {gallery.billeder.map((imgUrl, idx) => {
                const optimizedThumb = imgUrl.replace(
                  "assets.auxodev.dk/assets",
                  "assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,quality=80,format=auto/assets"
                );
                return (
                  <button
                    key={idx}
                    onClick={() => setLightboxIndex(idx)}
                    className="group relative aspect-square overflow-hidden rounded-xl border border-warm-beige bg-warm-beige shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  >
                    <img
                      src={optimizedThumb}
                      alt={`Galleri ${pageData.titel} ${idx + 1}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 flex items-center justify-center text-white opacity-0 group-hover:bg-black/30 group-hover:opacity-100 transition-all">
                      <i className="fa-solid fa-magnifying-glass-plus text-xl" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {lightboxIndex !== null && gallery?.billeder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-6 right-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark text-xl" />
          </button>

          {/* Prev Button */}
          <button
            onClick={handlePrevImage}
            className="absolute left-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-lg" />
          </button>

          {/* Active Image */}
          <div className="max-h-[85vh] max-w-[90vw] overflow-hidden rounded-lg">
            <img
              src={gallery.billeder[lightboxIndex].replace(
                "assets.auxodev.dk/assets",
                "assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets"
              )}
              alt={`Galleri stor visning ${lightboxIndex + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Next Button */}
          <button
            onClick={handleNextImage}
            className="absolute right-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-lg" />
          </button>

          {/* Image Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sm font-mono text-white/70">
            {lightboxIndex + 1} / {gallery.billeder.length}
          </div>
        </div>
      )}
    </div>
  );
}
